import React from 'react';
import {
  alpha,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Assignment,
  CalendarMonth,
  CheckCircle,
  Factory,
  InfoOutlined,
  Inventory2,
  Label,
  Link as LinkIcon,
  Restaurant,
  Science,
  Scale,
} from '@mui/icons-material';

const tabs = [
  { label: 'Details', icon: <InfoOutlined sx={{ fontSize: 14 }} />, active: true },
  { label: 'Usage', icon: <Restaurant sx={{ fontSize: 14 }} /> },
  { label: 'Documents', icon: <Assignment sx={{ fontSize: 14 }} /> },
  { label: 'Nutrition', icon: <Scale sx={{ fontSize: 14 }} /> },
  { label: 'Allergens', icon: <InfoOutlined sx={{ fontSize: 14 }} /> },
  { label: 'Validation', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
];

const baseProperties = [
  { label: 'Type', value: 'Substance' },
  { label: 'Physical state', value: 'Solid' },
  { label: 'Edible portion', value: '100%' },
];

const labellingFields = [
  { label: 'Legal name', value: 'Whey protein concentrate' },
  { label: 'Ingredient list name', value: 'Whey protein concentrate' },
];

const productionFields = [
  { label: 'Shelf life type', value: 'Best before' },
  { label: 'Cost / kg', value: 'Not defined' },
];

const usageLinks = [
  { label: 'Greek Style Yogurt', value: 'Prototype v3' },
  { label: 'Protein drink concept', value: 'Draft formula' },
];

function MiniValueCard({ label, value }) {
  return (
    <Box
      sx={{
        px: 0.9,
        py: 0.65,
        borderRadius: 1.5,
        bgcolor: (theme) => alpha(theme.palette.grey[500], 0.045),
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: 'block',
          fontWeight: 800,
          fontSize: 9.6,
          lineHeight: 1.1,
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mt: 0.25,
          fontWeight: 900,
          fontSize: 10.5,
          lineHeight: 1.15,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function SectionCard({ icon, title, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.1,
        borderRadius: 2,
        bgcolor: 'background.paper',
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={0.65} alignItems="center" sx={{ mb: 0.85 }}>
        <Box sx={{ color: 'primary.main', display: 'flex', flexShrink: 0 }}>
          {icon}
        </Box>

        <Typography
          variant="caption"
          sx={{
            fontWeight: 900,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 0.35,
          }}
        >
          {title}
        </Typography>
      </Stack>

      {children}
    </Paper>
  );
}

export default function IngredientRecordsVisual() {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 310,
        p: 1.6,
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          maxWidth: '100%',
          borderRadius: 2.5,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: '0 14px 28px rgba(15, 23, 42, 0.05)',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 1.7,
            py: 1.15,
            borderBottom: '1px solid',
            borderColor: 'divider',
            minWidth: 0,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Inventory2 sx={{ fontSize: 16 }} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Ingredient record
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mt: 0.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Structured details, properties, labelling fields and usage links
              </Typography>
            </Box>
          </Stack>

          <Chip
            label="Structured profile"
            size="small"
            sx={{
              height: 23,
              fontSize: 10.5,
              fontWeight: 800,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
              flexShrink: 0,
              ml: 1,
            }}
          />
        </Stack>

        {/* Tabs */}
        <Box
          sx={{
            px: 1.4,
            py: 0.65,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: (theme) => alpha(theme.palette.grey[500], 0.025),
            overflow: 'hidden',
          }}
        >
          <Stack direction="row" spacing={0.4} sx={{ minWidth: 0 }}>
            {tabs.map((tab) => (
              <Box
                key={tab.label}
                sx={{
                  px: 0.75,
                  py: 0.45,
                  borderRadius: 1.4,
                  color: tab.active ? 'primary.main' : 'text.secondary',
                  bgcolor: tab.active
                    ? (theme) => alpha(theme.palette.primary.main, 0.08)
                    : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.4,
                  fontSize: 9.6,
                  fontWeight: 900,
                  whiteSpace: 'nowrap',
                }}
              >
                {tab.icon}
                {tab.label}
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Body */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1.15fr 1fr' },
            gap: 1,
            p: 1.5,
            minHeight: 235,
            overflow: 'hidden',
          }}
        >
          {/* Left */}
          <Box sx={{ minWidth: 0 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.2,
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
                mb: 1,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.09),
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Science sx={{ fontSize: 16 }} />
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontWeight: 900,
                        fontSize: 11.4,
                        lineHeight: 1.15,
                      }}
                    >
                      Whey protein concentrate
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        fontSize: 9.8,
                        fontWeight: 700,
                        mt: 0.2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      High-protein dairy powder for nutritional formulations
                    </Typography>
                  </Box>
                </Stack>

                <Box
                  sx={{
                    px: 0.7,
                    py: 0.3,
                    borderRadius: 999,
                    bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
                    color: 'success.dark',
                    fontSize: 9.5,
                    fontWeight: 900,
                    flexShrink: 0,
                  }}
                >
                  Active
                </Box>
              </Stack>
            </Paper>

            <SectionCard icon={<InfoOutlined sx={{ fontSize: 15 }} />} title="Base properties">
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 0.7,
                }}
              >
                {baseProperties.map((item) => (
                  <MiniValueCard key={item.label} label={item.label} value={item.value} />
                ))}
              </Box>
            </SectionCard>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1,
                mt: 1,
              }}
            >
              <SectionCard icon={<Label sx={{ fontSize: 15 }} />} title="Labelling">
                <Stack spacing={0.55}>
                  {labellingFields.map((item) => (
                    <MiniValueCard key={item.label} label={item.label} value={item.value} />
                  ))}
                </Stack>
              </SectionCard>

              <SectionCard icon={<Factory sx={{ fontSize: 15 }} />} title="Production">
                <Stack spacing={0.55}>
                  {productionFields.map((item) => (
                    <MiniValueCard key={item.label} label={item.label} value={item.value} />
                  ))}
                </Stack>
              </SectionCard>
            </Box>
          </Box>

          {/* Right */}
          <Box sx={{ minWidth: 0 }}>
            <SectionCard icon={<CalendarMonth sx={{ fontSize: 15 }} />} title="Record metadata">
              <Stack spacing={0.65}>
                {[
                  { label: 'Last update', value: '23 Jun 2026' },
                  { label: 'Created by', value: 'Alex Carter' },
                ].map((item) => (
                  <MiniValueCard key={item.label} label={item.label} value={item.value} />
                ))}
              </Stack>
            </SectionCard>

            <Box sx={{ mt: 1 }}>
              <SectionCard icon={<LinkIcon sx={{ fontSize: 15 }} />} title="Linked usage">
                <Stack spacing={0.65}>
                  {usageLinks.map((item) => (
                    <Stack
                      key={item.label}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        px: 0.85,
                        py: 0.6,
                        borderRadius: 1.4,
                        bgcolor: (theme) => alpha(theme.palette.grey[500], 0.045),
                        minWidth: 0,
                      }}
                    >
                      <Stack direction="row" spacing={0.6} alignItems="center" sx={{ minWidth: 0 }}>
                        <Restaurant sx={{ fontSize: 13, color: 'primary.main', flexShrink: 0 }} />

                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 900,
                            fontSize: 10.2,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Stack>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 800,
                          fontSize: 9.8,
                          whiteSpace: 'nowrap',
                          ml: 0.8,
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </SectionCard>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}